import { IconChevronDown, IconMenu2 } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { authClient } from '@/auth/client';
import { LoginWrapper } from '@/components/auth/login-wrapper';
import { LocaleSwitcher } from '@/components/layout/locale-switcher';
import {
  flattenToolNavItems,
  TOOL_NAV_GROUPS,
  type ToolNavItem,
} from '@/components/navigation/tool-navigation';
import { CreditBadge } from '@/components/shared/credit-badge';
import { FreeCreditsButton } from '@/components/shared/free-credits-button';
import { Logo } from '@/components/shared/logo';
import { UserButton } from '@/components/shared/user-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { websiteConfig } from '@/config/website';
import {
  getLocale,
  getLocaleCopy,
  localizeHref,
  type Locale,
} from '@/lib/locale';
import { Routes } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { m } from '@/locale/paraglide/messages';

interface HomeHeaderCopy {
  videoAi: string;
  imageAi: string;
  howItWorks: string;
  pricing: string;
  login: string;
}

const HOME_HEADER_COPY: Record<Locale, HomeHeaderCopy> = {
  en: {
    videoAi: 'Video AI',
    imageAi: 'Image AI',
    howItWorks: 'How it works',
    pricing: 'Pricing',
    login: 'Login',
  },
  es: {
    videoAi: 'Video IA',
    imageAi: 'Imagen IA',
    howItWorks: 'Cómo funciona',
    pricing: 'Precios',
    login: 'Iniciar sesión',
  },
};

type HomepageToolNavCopy = {
  create: string;
  items: Record<string, { label: string; description: string }>;
};

const HOMEPAGE_TOOL_NAV_COPY: Record<Locale, HomepageToolNavCopy> = {
  en: {
    create: 'Create',
    items: {
      'image-to-video': {
        label: 'Image to Video',
        description: 'Animate a photo into a short AI video.',
      },
      'video-editor': {
        label: 'AI Video Editor',
        description: 'Edit a short video with a prompt and reference image.',
      },
      'text-to-video': {
        label: 'Text to Video',
        description: 'Generate a short AI video from a prompt.',
      },
      'reference-to-video': {
        label: 'Reference to Video',
        description:
          'Generate a MiniMax H3 video from images, clips, and audio.',
      },
    },
  },
  es: {
    create: 'Crear',
    items: {
      'image-to-video': {
        label: 'Imagen a video',
        description: 'Anima una foto en un video corto con IA.',
      },
      'video-editor': {
        label: 'Editor de video IA',
        description: 'Edita un video corto con un prompt y una referencia.',
      },
      'text-to-video': {
        label: 'Texto a video',
        description: 'Genera un video corto con IA desde un prompt.',
      },
    },
  },
};

export function HomeHeaderNav() {
  const { headerLinkItems, toolGroups } = getHomepageHeaderNavigation();

  return (
    <>
      <a
        href={getLocalizedHomepageHref('/')}
        aria-label={m.common_home()}
        className="flex min-w-0 items-center gap-2 md:hidden"
      >
        <Logo className="size-9 shrink-0 rounded-md" />
        <span className="truncate font-serif font-bold text-[1.35rem] text-foreground tracking-normal">
          {websiteConfig.metadata?.name}
        </span>
      </a>
      <nav
        aria-label={m.common_main_navigation()}
        className="hidden min-w-0 flex-1 items-center justify-end gap-1 overflow-visible pr-1 md:flex lg:gap-3"
      >
        {toolGroups.map((group) => (
          <HomeToolGroupMenu key={group.key} group={group} />
        ))}
        {headerLinkItems.map((item) => (
          <a
            key={item.label}
            href={getLocalizedHomepageHref(item.href)}
            className="inline-flex h-10 shrink-0 items-center rounded-md px-1.5 font-semibold text-foreground/82 text-sm transition hover:text-foreground sm:px-2 sm:text-base"
          >
            {item.label}
          </a>
        ))}
      </nav>
    </>
  );
}

function getHomepageToolNavCopy() {
  return HOMEPAGE_TOOL_NAV_COPY[getLocale()] ?? HOMEPAGE_TOOL_NAV_COPY.en;
}

function getLocalizedHomepageToolGroups(
  navCopy: HomeHeaderCopy,
  toolCopy: HomepageToolNavCopy
) {
  const localizeItem = (item: ToolNavItem): ToolNavItem => ({
    ...item,
    label: toolCopy.items[item.key]?.label ?? item.label,
    description: toolCopy.items[item.key]?.description ?? item.description,
    children: item.children?.map(localizeItem),
  });

  return TOOL_NAV_GROUPS.map((group) => ({
    ...group,
    label:
      group.key === 'video-ai'
        ? navCopy.videoAi
        : group.key === 'image-ai'
          ? navCopy.imageAi
          : group.label,
    items: flattenToolNavItems(group.items.map(localizeItem)),
  }));
}

function getHomepageHeaderNavigation() {
  const copy = getLocaleCopy(HOME_HEADER_COPY);
  const toolGroups = getLocalizedHomepageToolGroups(
    copy,
    getHomepageToolNavCopy()
  );
  const headerLinkItems = [
    { label: copy.howItWorks, href: Routes.Root + '#how-it-works' },
    { label: copy.pricing, href: Routes.Pricing },
  ];

  return { copy, headerLinkItems, toolGroups };
}

function HomeMobileMenu({
  className,
  accountSlot,
  headerLinkItems,
  toolGroups,
}: {
  className?: string;
  accountSlot?: ReactNode;
  headerLinkItems: { label: string; href: string }[];
  toolGroups: ReturnType<typeof getLocalizedHomepageToolGroups>;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        type="button"
        aria-label={m.common_toggle_menu()}
        className={cn(
          'inline-flex size-10 shrink-0 items-center justify-center rounded-md text-foreground/88 transition hover:bg-muted/65 hover:text-foreground',
          className
        )}
      >
        <IconMenu2 className="size-6" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className="w-[min(20rem,calc(100vw-2rem))] p-2"
      >
        {toolGroups.map((group, index) => (
          <DropdownMenuGroup key={group.key}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
            {group.items.map((item) => {
              const ItemIcon = item.icon;
              return (
                <DropdownMenuItem key={item.key} className="p-0">
                  <a
                    href={getLocalizedHomepageHref(item.href)}
                    className="flex w-full items-start gap-2 rounded-md px-2 py-2"
                  >
                    <ItemIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0">
                      <span className="block font-medium text-foreground text-sm">
                        {item.label}
                      </span>
                      <span className="mt-0.5 block text-muted-foreground text-xs leading-snug">
                        {item.description}
                      </span>
                    </span>
                  </a>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuGroup>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {headerLinkItems.map((item) => (
            <DropdownMenuItem key={item.label} className="p-0">
              <a
                href={getLocalizedHomepageHref(item.href)}
                className="flex w-full items-center rounded-md px-2 py-2 font-medium text-foreground text-sm"
              >
                {item.label}
              </a>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
        {accountSlot}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function HomeToolGroupMenu({
  group,
}: {
  group: ReturnType<typeof getLocalizedHomepageToolGroups>[number];
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-md px-1.5 font-semibold text-foreground/82 text-sm transition hover:bg-muted/60 hover:text-foreground sm:px-2 sm:text-base">
        {group.label}
        <IconChevronDown className="size-4 text-foreground/75" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60">
        {group.items.map((item) => {
          const ItemIcon = item.icon;
          return (
            <DropdownMenuItem key={item.key} className="p-0">
              <a
                href={getLocalizedHomepageHref(item.href)}
                className="flex w-full items-start gap-2 rounded-md px-2 py-2"
              >
                <ItemIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0">
                  <span className="block font-medium text-foreground text-sm">
                    {item.label}
                  </span>
                  <span className="mt-0.5 block text-muted-foreground text-xs leading-snug">
                    {item.description}
                  </span>
                </span>
              </a>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function HomeHeaderActions() {
  const { copy, headerLinkItems, toolGroups } = getHomepageHeaderNavigation();
  const [mounted, setMounted] = useState(false);
  const { data: session, isPending } = authClient.useSession();
  const user = session?.user;
  const authEnabled = websiteConfig.auth?.enable ?? false;
  const authPending = !mounted || isPending;

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className="flex shrink-0 items-center gap-2 sm:gap-3">
      <HomeMobileMenu
        accountSlot={
          authEnabled ? (
            <>
              <DropdownMenuSeparator />
              <div className="flex flex-col gap-2 px-1.5 py-2">
                <LocaleSwitcher />
                {authPending ? (
                  <Skeleton className="h-10 w-full rounded-md" />
                ) : user ? (
                  <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/35 px-2.5 py-2">
                    <span className="min-w-0 truncate font-medium text-sm">
                      {user.name}
                    </span>
                    <div className="flex shrink-0 items-center gap-2">
                      <CreditBadge />
                      <UserButton user={user} />
                    </div>
                  </div>
                ) : (
                  <>
                    <LoginWrapper mode="modal" asChild>
                      <button
                        type="button"
                        className="inline-flex h-10 w-full items-center justify-center rounded-md border font-semibold text-sm transition hover:bg-muted/70"
                      >
                        {copy.login}
                      </button>
                    </LoginWrapper>
                    <FreeCreditsButton className="h-10 w-full justify-center rounded-md px-3" />
                  </>
                )}
              </div>
            </>
          ) : null
        }
        className="md:hidden"
        headerLinkItems={headerLinkItems}
        toolGroups={toolGroups}
      />
      <div className="hidden shrink-0 items-center gap-2 sm:gap-3 md:flex">
        {authEnabled ? (
          authPending ? (
            <Skeleton className="h-8 w-14 rounded-md" />
          ) : !user ? (
            <LoginWrapper mode="modal" asChild>
              <button
                type="button"
                className="h-10 items-center rounded-md px-1.5 font-semibold text-sm text-foreground/70 transition hover:text-foreground md:inline-flex"
              >
                {copy.login}
              </button>
            </LoginWrapper>
          ) : null
        ) : null}
        <LocaleSwitcher />
        {authEnabled ? (
          <>
            <div className="hidden h-8 w-px bg-border/80 md:block" />
            {authPending ? (
              <Skeleton className="size-8 rounded-full" />
            ) : user ? (
              <>
                <CreditBadge />
                <UserButton user={user} />
              </>
            ) : (
              <FreeCreditsButton />
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

function getLocalizedHomepageHref(href: string) {
  return href.startsWith('#') ? href : localizeHref(href);
}
